import React, { useState } from "react";
import { auth, db } from "../../../firebase";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";
import { useNavigate, useSearchParams } from "react-router-dom";
import AuthShell from "../components/AuthShell";
import { gymConfig } from "../../../config/gymConfig";
import { upsertPublicProfile } from "../../../lib/publicProfiles";

const Signup = () => {
  const [searchParams] = useSearchParams();
  const invitedEmail = searchParams.get("email")?.trim().toLowerCase() ?? "";
  const [email, setEmail] = useState(invitedEmail);
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setLoading(true);
      setError("");
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const uid = userCredential.user.uid;

      await setDoc(doc(db, "users", uid), {
        email,
        name: name.trim(),
        role: "user",
        approvalStatus: "pending",
      });
      await upsertPublicProfile({
        uid,
        name: name.trim(),
        photoURL: null,
      });

      navigate("/join", { replace: true });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      eyebrow="Create Account"
      title={`Welcome to ${gymConfig.brand.name}.`}
      description="Create your account first, then choose a membership to unlock the full app."
      footerPrompt="Already have an account?"
      footerLabel="Log in"
      footerTo="/login"
    >
      <form onSubmit={handleSignup} className="space-y-5">
        <div>
          <h2 className="text-2xl font-heading tracking-tight text-white">Join {gymConfig.brand.name}</h2>
          <p className="mt-2 text-sm leading-6 text-[color:var(--color-text-subtle)]">
            Once your account is created, you can choose a membership and activate access straight away.
          </p>
        </div>

        {error ? (
          <div className="app-status-danger rounded-2xl border px-4 py-3 text-sm">
            {error}
          </div>
        ) : null}

        <label className="block">
          <span className="mb-2 block text-sm font-medium text-[color:var(--color-text-muted)]">Full name</span>
          <input
            type="text"
            placeholder=""
            autoComplete="name"
            className="app-input w-full rounded-2xl border px-4 py-3 text-white outline-none transition placeholder:text-[color:var(--color-text-subtle)] focus:bg-[color:var(--color-surface-overlay-strong)]"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </label>

        <label className="block">
          <span className="mb-2 block text-sm font-medium text-[color:var(--color-text-muted)]">Email</span>
          <input
            type="email"
            placeholder="you@example.com"
            autoComplete="email"
            inputMode="email"
            className="app-input w-full rounded-2xl border px-4 py-3 text-white outline-none transition placeholder:text-[color:var(--color-text-subtle)] focus:bg-[color:var(--color-surface-overlay-strong)]"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </label>

        {invitedEmail ? (
          <div className="app-status-success rounded-2xl border px-4 py-3 text-sm leading-6">
            This email was prefilled from your invite. Create your account here, then continue to membership setup.
          </div>
        ) : null}

        <label className="block">
          <span className="mb-2 block text-sm font-medium text-[color:var(--color-text-muted)]">Password</span>
          <input
            type="password"
            placeholder="Choose a secure password"
            autoComplete="new-password"
            className="app-input w-full rounded-2xl border px-4 py-3 text-white outline-none transition placeholder:text-[color:var(--color-text-subtle)] focus:bg-[color:var(--color-surface-overlay-strong)]"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </label>

        <div className="app-status-primary rounded-2xl border px-4 py-3 text-sm leading-6">
          After sign-up, you’ll go straight to the membership page so you can activate access.
        </div>

        <button
          type="submit"
          disabled={loading}
          className="app-button-primary w-full rounded-2xl px-4 py-3 font-semibold transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-70"
        >
          {loading ? "Creating account..." : "Create Account"}
        </button>
      </form>
    </AuthShell>
  );
};

export default Signup;
